import { Avatar, Box, Divider, IconButton, List, Stack } from "@mui/material";
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import colorConfigs from "../../configs/colorConfigs";
import sizeConfigs from "../../configs/sizeConfigs";
import SidebarItem from "./SidabarItem";
import SidebarItemCollapse from "./SidebarItemCollapse";
import appRoutes from "../../routes/appRoutes";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useMemo } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import { canAccessRoute } from "../../routes/routeAccess";
import { useSelector } from "react-redux";
import { selectCurrentLogo } from "../../Redux/selectors/brandingSelectors";

const drawerWidth = sizeConfigs.sidebar.width;


const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    color: colorConfigs.sidebar.color
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    color: colorConfigs.sidebar.color
});


const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));




const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        color: colorConfigs.sidebar.color,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        "& .MuiTypography-body1": {
            color: "white"
        },
        // '& .MuiDivider-root': {
        //     backgroundColor: colorConfigs.sidebar.color,
        // },
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);


export default function Sidebar({ open, setOpen }) {
    const user = useSelector((state: any) => state.general.user);
    const isPartnerLoggedIn = useSelector((state: any) => state.partner.isPartnerLoggedIn);
    const logoToShow = useSelector(selectCurrentLogo);
    const visibleRoutes = useMemo(() => {
        if (!user) return null;
        if (!isPartnerLoggedIn) return appRoutes;
        const filter = (routes) =>
            routes
                .filter((r) => r.sidebarProps)
                .filter((r) => canAccessRoute(user, r))
                .map((r) => ({
                    ...r,
                    child: r.child ? filter(r.child) : r.child,
                }))
                .filter((r) => {
                    if (r.child && r.child.length === 0) return false;
                    return true;
                });
        return filter(appRoutes);
    }, [user]);
    const theme = useTheme();
    // const [open, setOpen] = useState(false);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };


    return (
        <Drawer
            open={open}
            variant="permanent"
        >
            {!open && <DrawerHeader sx={{ justifyContent: "center" }}>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}>
                    <MenuIcon />
                </IconButton>
            </DrawerHeader>}
            {open && <DrawerHeader>
                <Stack
                    sx={{ width: "100%" }}
                    direction="row"
                    justifyContent="center"
                >
                    <Avatar src={logoToShow} sx={{ ...(!open && { display: 'none' }), transform: "scale(0.9)" }} />
                </Stack>

                <IconButton onClick={handleDrawerClose} sx={{ ...(!open && { display: 'none' }) }}>
                    <ChevronLeftIcon sx={{ color: 'white' }} />
                </IconButton>
            </DrawerHeader>}
            {/* <Divider sx={{ ...(open && { display: 'none' }) }} /> */}
            <List disablePadding>
                {visibleRoutes ?
                    visibleRoutes.map((route, index) =>
                        route.child ? (
                            <SidebarItemCollapse item={route} openDrawer={open} setOpenDrawer={setOpen} key={index} />
                        ) : (
                            <SidebarItem item={route} open={open} setOpen={setOpen} key={index} />
                        )
                    ) : null}
            </List>
        </Drawer>
    );
};
